import { lazy, Suspense } from "react";
import { Navigate, RouteObject, useParams } from "react-router-dom";
import AdminGuard from "../components/admin/AdminGuard";
import LoadingScreen from "@/components/feature/LoadingScreen";

const HomePage = lazy(() => import("../pages/home/page"));
const NotFound = lazy(() => import("../pages/NotFound"));
const AdminLogin = lazy(() => import("../pages/admin/login/page"));
const AdminDashboard = lazy(() => import("../pages/admin/dashboard/page"));
const AdminClients = lazy(() => import("../pages/admin/clients/page"));
const AdminClientDetail = lazy(() => import("../pages/admin/clients/detail/page"));
const AdminProducts = lazy(() => import("../pages/admin/products/page"));
const AdminProductsEngine = lazy(() => import("../pages/admin/products/engine/page"));
const AdminPromotions = lazy(() => import("../pages/admin/promotions/page"));
const AdminLeads = lazy(() => import("../pages/admin/leads/page"));
const AdminInvoices = lazy(() => import("../pages/admin/invoices/page"));
const AdminInvoiceDetail = lazy(() => import("../pages/admin/invoices/detail/page"));
const AdminPayments = lazy(() => import("../pages/admin/payments/page"));
const AdminOrders = lazy(() => import("../pages/admin/orders/page"));
const AdminOrderDetail = lazy(() => import("../pages/admin/orders/detail/page"));
const AdminReceipts = lazy(() => import("../pages/admin/receipts/page"));
const AdminReports = lazy(() => import("../pages/admin/reports/page"));
const AdminPaymentSettings = lazy(() => import("../pages/admin/settings/payments/page"));
const AdminSettings = lazy(() => import("../pages/admin/settings/page"));
const AdminLoyaltyCards = lazy(() => import("../pages/admin/loyalty/cards/page"));
const AdminLoyaltyClients = lazy(() => import("../pages/admin/loyalty/clients/page"));
const AdminGiftCards = lazy(() => import("../pages/admin/loyalty/gift-cards/page"));
const AdminCardBatches = lazy(() => import("../pages/admin/loyalty/gift-cards/batches/page"));
const AdminCardPrintStudio = lazy(() => import("../pages/admin/loyalty/gift-cards/print-studio/page"));
const AdminStoreCredits = lazy(() => import("../pages/admin/store-credits/page"));
const AdminRefunds = lazy(() => import("../pages/admin/refunds/page"));
const AdminMarketing = lazy(() => import("../pages/admin/marketing/page"));
const TrackPage = lazy(() => import("../pages/track/page"));
const PortalLogin = lazy(() => import("../pages/portal/login/page"));
const PortalRegister = lazy(() => import("../pages/portal/register/page"));
const PortalResetPassword = lazy(() => import("../pages/portal/reset-password/page"));
const PortalDashboard = lazy(() => import("../pages/portal/page"));
const PortalInvoices = lazy(() => import("../pages/portal/invoices/page"));
const PortalInvoiceDetail = lazy(() => import("../pages/portal/invoices/detail/page"));
const PortalOrders = lazy(() => import("../pages/portal/orders/page"));
const PortalOrderDetail = lazy(() => import("../pages/portal/orders/detail/page"));
const PortalGiftCards = lazy(() => import("../pages/portal/gift-cards/page"));
const PortalPOS = lazy(() => import("../pages/portal/pos/page"));
const PortalSettings = lazy(() => import("../pages/portal/settings/page"));
const PortalQRCodes = lazy(() => import("../pages/portal/qr-codes/page"));
const KioskPage = lazy(() => import("../pages/kiosk/page"));
const AdminExpenses = lazy(() => import("../pages/admin/expenses/page"));
const AdminKiosk = lazy(() => import("../pages/admin/kiosk/page"));
const AdminSupport = lazy(() => import("../pages/admin/support/page"));
const AdminSupportHistory = lazy(() => import("../pages/admin/support/history/page"));
const AdminAppointments = lazy(() => import("../pages/admin/appointments/page"));
const AdminAiSales = lazy(() => import("../pages/admin/ai-sales/page"));
const PublicCatalog = lazy(() => import("../pages/public/CatalogPage"));
const DesignApprovalPage = lazy(() => import("../pages/public/DesignApprovalPage"));
const ConfigureRedirectPage = lazy(() => import("../pages/public/ConfigureRedirectPage"));
const EngineProductPage = lazy(() => import("../pages/public/EngineProductPage"));
const OfferLandingPage = lazy(() => import("../pages/public/OfferLandingPage"));
const MyBussHubPage = lazy(() => import("../pages/mybusshub/page"));
const MyBussHubDashboard = lazy(() => import("../pages/mybusshub/dashboard/page"));
const MyBussHubPrinting = lazy(() => import("../pages/mybusshub/printing/page"));
const MyBussHubOrders = lazy(() => import("../pages/mybusshub/orders/page"));
const MyBussHubOrderDetail = lazy(() => import("../pages/mybusshub/orders/detail/page"));
const AdminMyBussHub = lazy(() => import("../pages/admin/mybusshub/page"));
const AdminCatalog = lazy(() => import("../pages/admin/catalog/page"));
const AdminWhatsAppSettings = lazy(() => import("../pages/admin/settings/whatsapp/page"));
const AdminNotifications = lazy(() => import("../pages/admin/notifications/page"));
const AdminSupplierEmailAutomation = lazy(() => import("../pages/admin/settings/supplier-email-automation/page"));
const AdminSoftwareDownloads = lazy(() => import("../pages/admin/settings/software-downloads/page"));
const AdminQuotes = lazy(() => import("../pages/admin/quotes/page"));
const AdminQuoteDetail = lazy(() => import("../pages/admin/quotes/detail/page"));
const AdminQAExecutivePipeline = lazy(() => import("../pages/admin/qa-executive-pipeline/page"));
const AdminQAVoicePipeline = lazy(() => import("../pages/admin/qa-voice-pipeline/page"));
const CheckoutSuccessPage = lazy(() => import("../pages/public/CheckoutSuccessPage"));
const LegalTermsPage = lazy(() => import("../pages/public/legal/TermsPage"));
const LegalPrivacyPage = lazy(() => import("../pages/public/legal/PrivacyPage"));
const QRRedirectPage = lazy(() => import("../pages/public/QRRedirectPage"));
const UnsubscribePage = lazy(() => import("../pages/public/UnsubscribePage"));
const AdminProductFamilies = lazy(() => import("../pages/admin/catalog/families/page"));
const AdminCustomerReminders = lazy(() => import("../pages/admin/customer-reminders/page"));
const AdminQRCodes = lazy(() => import("../pages/admin/qr-codes/page"));
const AdminQRCreate = lazy(() => import("../pages/admin/qr-codes/create/page"));
const AdminQREdit = lazy(() => import("../pages/admin/qr-codes/edit/page"));
const AdminQRAnalytics = lazy(() => import("../pages/admin/qr-codes/analytics/page"));
const AdminQRAnalyticsGeneral = lazy(() => import("../pages/admin/qr-analytics/page"));

const Loader = () => <LoadingScreen />;

/** Enlace antiguo /configure/product/:id → configurador oficial /product/:id */
function LegacyConfigureProductRedirect() {
  const { productId } = useParams<{ productId: string }>();
  return <Navigate to={`/product/${productId ?? ''}`} replace />;
}

const routes: RouteObject[] = [
  {
    path: "/",
    element: <Suspense fallback={<Loader />}><HomePage /></Suspense>,
  },
  {
    path: "/track/:code",
    element: <Suspense fallback={<Loader />}><TrackPage /></Suspense>,
  },
  {
    path: "/track",
    element: <Suspense fallback={<Loader />}><TrackPage /></Suspense>,
  },
  {
    path: "/catalogo",
    element: <Suspense fallback={<Loader />}><PublicCatalog /></Suspense>,
  },
  {
    path: "/design-approval/:token",
    element: <Suspense fallback={<Loader />}><DesignApprovalPage /></Suspense>,
  },
  {
    // Compatibilidad: los enlaces antiguos llegan al producto OFICIAL.
    // Ya NO existe un configurador duplicado en esta ruta.
    path: "/configure/:slug",
    element: <Suspense fallback={<Loader />}><ConfigureRedirectPage /></Suspense>,
  },
  {
    path: "/configure/product/:productId",
    element: <LegacyConfigureProductRedirect />,
  },
  {
    // Motor nuevo (simpleEngine): página pública del producto del motor.
    path: "/product/:productId",
    element: <Suspense fallback={<Loader />}><EngineProductPage /></Suspense>,
  },
  {
    // Landing pública de OFERTA: reutiliza el mismo configurador y checkout.
    path: "/ofertas/:slug",
    element: <Suspense fallback={<Loader />}><OfferLandingPage /></Suspense>,
  },
  {
    path: "/checkout/success",
    element: <Suspense fallback={<Loader />}><CheckoutSuccessPage /></Suspense>,
  },
  // ── Documentos legales reales (Bloque 34) ────────────────────────────────
  {
    path: "/terminos-y-condiciones",
    element: <Suspense fallback={<Loader />}><LegalTermsPage /></Suspense>,
  },
  {
    path: "/politica-de-privacidad",
    element: <Suspense fallback={<Loader />}><LegalPrivacyPage /></Suspense>,
  },
  {
    path: "/unsubscribe",
    element: <Suspense fallback={<Loader />}><UnsubscribePage /></Suspense>,
  },
  {
    path: "/mybusshub",
    element: <Suspense fallback={<Loader />}><MyBussHubPage /></Suspense>,
  },
  {
    path: "/mybusshub/dashboard",
    element: <Suspense fallback={<Loader />}><MyBussHubDashboard /></Suspense>,
  },
  {
    path: "/mybusshub/printing",
    element: <Suspense fallback={<Loader />}><MyBussHubPrinting /></Suspense>,
  },
  {
    path: "/mybusshub/orders",
    element: <Suspense fallback={<Loader />}><MyBussHubOrders /></Suspense>,
  },
  {
    path: "/mybusshub/orders/:id",
    element: <Suspense fallback={<Loader />}><MyBussHubOrderDetail /></Suspense>,
  },
  // ── Public QR redirect ──────────────────────────────────────────────
  {
    path: "/r/:dynamic_code",
    element: <Suspense fallback={<Loader />}><QRRedirectPage /></Suspense>,
  },
  {
    path: "/kiosk",
    element: <Suspense fallback={<Loader />}><KioskPage /></Suspense>,
  },
  // ── Admin login (sin guard) ──────────────────────────────────────────────
  {
    path: "/admin/login",
    element: <Suspense fallback={<Loader />}><AdminLogin /></Suspense>,
  },
  // ── Admin rutas protegidas ───────────────────────────────────────────────
  {
    path: "/admin/dashboard",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminDashboard /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/clients",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminClients /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/clients/:id",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminClientDetail /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/products",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminProducts /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/products/engine",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminProductsEngine /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/promotions",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminPromotions /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/leads",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminLeads /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/invoices",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminInvoices /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/invoices/:id",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminInvoiceDetail /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/payments",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminPayments /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/orders",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminOrders /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/orders/:id",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminOrderDetail /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/quotes",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminQuotes /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/quotes/:id",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminQuoteDetail /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/receipts",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminReceipts /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/reports",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminReports /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/expenses",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminExpenses /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/settings",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminSettings /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/settings/payments",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminPaymentSettings /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/settings/whatsapp",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminWhatsAppSettings /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/settings/supplier-email-automation",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminSupplierEmailAutomation /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/settings/software-downloads",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminSoftwareDownloads /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/notifications",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminNotifications /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/loyalty/cards",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminLoyaltyCards /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/loyalty/clients",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminLoyaltyClients /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/loyalty/gift-cards",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminGiftCards /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/loyalty/gift-cards/batches",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminCardBatches /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/loyalty/gift-cards/print-studio",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminCardPrintStudio /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/store-credits",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminStoreCredits /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/refunds",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminRefunds /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/marketing",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminMarketing /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/catalog",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminCatalog /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/catalog/families",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminProductFamilies /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/support",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminSupport /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/support/history",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminSupportHistory /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/appointments",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminAppointments /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/ai-sales",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminAiSales /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/qa-executive-pipeline",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminQAExecutivePipeline /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/qa-voice-pipeline",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminQAVoicePipeline /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/customer-reminders",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminCustomerReminders /></Suspense></AdminGuard>,
  },
  // ── QR Designer ─────────────────────────────────────────────────────────
  {
    path: "/admin/qr-codes",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminQRCodes /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/qr-codes/create",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminQRCreate /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/qr-codes/:id/edit",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminQREdit /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/qr-codes/:id/analytics",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminQRAnalytics /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/qr-analytics",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminQRAnalyticsGeneral /></Suspense></AdminGuard>,
  },
  // ── Kiosk Admin ─────────────────────────────────────────────────────────
  {
    path: "/admin/mybusshub",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminMyBussHub /></Suspense></AdminGuard>,
  },
  {
    // Detalle del Pedido MyBussHub — reutiliza el detalle de Quote existente
    // (misma entidad ip_quotes + ip_quote_items, misma conversión a Invoice).
    path: "/admin/mybusshub/orders/:id",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminQuoteDetail /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/kiosk",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminKiosk /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/kiosk/orders",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminKiosk /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/kiosk/designs",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminKiosk /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/kiosk/products",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminKiosk /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/kiosk/stamp-prices",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminKiosk /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/kiosk/mockups",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminKiosk /></Suspense></AdminGuard>,
  },
  {
    path: "/admin/kiosk/ai-credits",
    element: <AdminGuard><Suspense fallback={<Loader />}><AdminKiosk /></Suspense></AdminGuard>,
  },
  // ── Portal ───────────────────────────────────────────────────────────────
  {
    path: "/portal/login",
    element: <Suspense fallback={<Loader />}><PortalLogin /></Suspense>,
  },
  {
    path: "/portal/register",
    element: <Suspense fallback={<Loader />}><PortalRegister /></Suspense>,
  },
  {
    path: "/portal/reset-password",
    element: <Suspense fallback={<Loader />}><PortalResetPassword /></Suspense>,
  },
  {
    path: "/portal",
    element: <Suspense fallback={<Loader />}><PortalDashboard /></Suspense>,
  },
  {
    path: "/portal/invoices",
    element: <Suspense fallback={<Loader />}><PortalInvoices /></Suspense>,
  },
  {
    path: "/portal/invoices/:id",
    element: <Suspense fallback={<Loader />}><PortalInvoiceDetail /></Suspense>,
  },
  {
    path: "/portal/orders",
    element: <Suspense fallback={<Loader />}><PortalOrders /></Suspense>,
  },
  {
    path: "/portal/orders/:id",
    element: <Suspense fallback={<Loader />}><PortalOrderDetail /></Suspense>,
  },
  {
    path: "/portal/gift-cards",
    element: <Suspense fallback={<Loader />}><PortalGiftCards /></Suspense>,
  },
  {
    path: "/portal/pos",
    element: <Suspense fallback={<Loader />}><PortalPOS /></Suspense>,
  },
  {
    path: "/portal/settings",
    element: <Suspense fallback={<Loader />}><PortalSettings /></Suspense>,
  },
  {
    path: "/portal/qr-codes",
    element: <Suspense fallback={<Loader />}><PortalQRCodes /></Suspense>,
  },
  {
    path: "*",
    element: <Suspense fallback={<Loader />}><NotFound /></Suspense>,
  },
];

export default routes;